import { useState, type FormEvent, type ReactNode } from 'react'
import {
  Eye,
  EyeOff,
  KeyRound,
  Link2,
  LogOut,
  Moon,
  Save,
  Server,
  Sun,
  Palette
} from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { credentialsSaved, disconnected } from '@/features/connection/connectionSlice'
import { themeToggled, toastPushed } from '@/features/ui/uiSlice'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardHeader, DataRow } from '@/components/ui/Surface'
import { Button } from '@/components/ui/Button'
import { Field, TextInput } from '@/components/ui/Field'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Badge } from '@/components/ui/Badge'
import { formatRelative } from '@/lib/format'

export function SettingsPage(): ReactNode {
  const dispatch = useAppDispatch()
  const connection = useAppSelector((s) => s.connection)
  const theme = useAppSelector((s) => s.ui.theme)

  const [cloudUrl, setCloudUrl] = useState(connection.cloudUrl)
  const [adminKey, setAdminKey] = useState(connection.adminKey)
  const [reveal, setReveal] = useState(false)
  const [confirmingSignOut, setConfirmingSignOut] = useState(false)

  const dirty =
    cloudUrl.trim().replace(/\/+$/, '') !== connection.cloudUrl ||
    adminKey.trim() !== connection.adminKey
  const valid = cloudUrl.trim().length > 0 && adminKey.trim().length > 0

  const save = (e: FormEvent): void => {
    e.preventDefault()
    if (!valid) return
    dispatch(credentialsSaved({ cloudUrl, adminKey }))
    dispatch(toastPushed('Connection settings saved'))
  }

  return (
    <>
      <PageHeader
        title="Settings"
        description="Where this portal points, and how it authenticates."
      />

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Server connection"
            description="Applied immediately and remembered in this browser."
            icon={Server}
          />
          <form onSubmit={save} className="space-y-4 p-4">
            <Field label="Cloud server URL" hint="The API base, ending in /api/v2.">
              {(id) => (
                <TextInput
                  id={id}
                  icon={Link2}
                  value={cloudUrl}
                  onChange={(e) => setCloudUrl(e.target.value)}
                  placeholder="https://your-server.example.com/api/v2"
                  spellCheck={false}
                  autoComplete="off"
                />
              )}
            </Field>

            <Field label="Admin key" hint="Must match ADMIN_API_KEY on the server.">
              {(id) => (
                <TextInput
                  id={id}
                  icon={KeyRound}
                  type={reveal ? 'text' : 'password'}
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                  trailing={
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      icon={reveal ? EyeOff : Eye}
                      onClick={() => setReveal((v) => !v)}
                      aria-label={reveal ? 'Hide key' : 'Show key'}
                    />
                  }
                />
              )}
            </Field>

            <div className="flex items-center gap-2 pt-1">
              <Button type="submit" variant="primary" icon={Save} disabled={!valid || !dirty}>
                Save and reconnect
              </Button>
              <Button
                type="button"
                variant="subtle"
                icon={LogOut}
                onClick={() => setConfirmingSignOut(true)}
              >
                Forget key
              </Button>
            </div>
          </form>
        </Card>

        <div className="space-y-3">
          <Card>
            <CardHeader title="Link status" icon={Server} />
            <dl className="divide-y divide-line px-4 py-2">
              <DataRow label="State">
                {connection.status === 'online' ? (
                  <Badge tone="green">Connected</Badge>
                ) : connection.status === 'offline' ? (
                  <Badge tone="red">Offline</Badge>
                ) : connection.status === 'checking' ? (
                  <Badge tone="amber">Linking</Badge>
                ) : (
                  <Badge>Idle</Badge>
                )}
              </DataRow>
              <DataRow label="Last successful call">
                {connection.lastSyncedAt ? formatRelative(connection.lastSyncedAt) : 'never'}
              </DataRow>
              <DataRow label="Last error">
                <span className="text-negative">{connection.lastError ?? '—'}</span>
              </DataRow>
            </dl>
          </Card>

          <Card>
            <CardHeader title="Appearance" icon={Palette} />
            <div className="flex items-center justify-between gap-4 p-4">
              <div>
                <p className="text-[12.5px] font-medium text-fg">Theme</p>
                <p className="text-[11.5px] text-muted">
                  Currently {theme}. Remembered on this device.
                </p>
              </div>
              <Button
                variant="secondary"
                icon={theme === 'dark' ? Sun : Moon}
                onClick={() => dispatch(themeToggled())}
              >
                Switch to {theme === 'dark' ? 'light' : 'dark'}
              </Button>
            </div>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={confirmingSignOut}
        title="Forget the admin key?"
        message="The key will be cleared from this browser and the portal will return to the connect screen. Nothing on the server changes."
        confirmLabel="Forget key"
        onCancel={() => setConfirmingSignOut(false)}
        onConfirm={() => {
          setConfirmingSignOut(false)
          dispatch(disconnected())
        }}
      />
    </>
  )
}
